/**
 * StreamdownRenderer Component
 *
 * A reusable component for rendering Markdown content using Streamdown
 * Designed for AI-streamed content with proper handling of unterminated chunks
 *
 * Features:
 * - GitHub Flavored Markdown (tables, task lists, etc.)
 * - Syntax-highlighted code blocks with copy button
 * - Math support (LaTeX expressions)
 * - Security hardening for untrusted content
 * - Tailwind typography styles
 * - Resilient to Shiki code-block chunk-load failures (Vite 504 / stale dep cache)
 */

import React, { Component, ReactNode } from 'react';
import { Streamdown } from 'streamdown';

interface StreamdownRendererProps {
  /**
   * Markdown content to render
   */
  content: string;

  /**
   * Optional className for styling the container
   */
  className?: string;

  /**
   * Rendering mode for Streamdown
   * - 'static': Pre-rendered markdown (default)
   * - 'streaming': For AI-generated content with incomplete chunks
   * @default 'static'
   */
  mode?: 'static' | 'streaming';
}

/**
 * Local error boundary around Streamdown. When Streamdown's lazy Shiki
 * code-block chunk fails to load (Vite 504 "Outdated Optimize Dep", a dropped
 * connection, or any render error inside the markdown renderer), degrade to a
 * readable plain-text fallback instead of crashing the whole modal/tab/page.
 *
 * `key={content}` resets the boundary when content changes, so a transient
 * chunk failure on one render doesn't permanently lock the fallback in.
 */
interface BoundaryState { hasError: boolean }
class StreamdownBoundary extends Component<{ content: string; children: ReactNode }, BoundaryState> {
  state: BoundaryState = { hasError: false };
  static getDerivedStateFromError(): BoundaryState { return { hasError: true }; }
  componentDidCatch(error: unknown) {
    // Surface the underlying failure (Vite dep cache, network, or a markdown
    // rendering bug) in the console — never silently swallow it.
    console.error('Streamdown render failed, falling back to plain text:', error);
  }
  render() {
    if (this.state.hasError) {
      // Preserve the user's data visibility: show the raw markdown as text.
      const text = typeof this.props.content === 'string' ? this.props.content : '';
      return text ? (
        <div className="whitespace-pre-wrap break-words text-[13px] leading-6 text-white/80">
          {text}
        </div>
      ) : null;
    }
    return this.props.children;
  }
}

export const StreamdownRenderer: React.FC<StreamdownRendererProps> = ({
  content,
  className = '',
  mode = 'static'
}) => {
  return (
    <div className={`streamdown-container ${className}`}>
      <StreamdownBoundary content={content} key={content}>
        <Streamdown
          mode={mode}
          // Tailwind prose styles for typography
          className="prose prose-invert max-w-none text-gray-300 leading-relaxed text-sm
                     prose-headings:text-white prose-headings:font-semibold
                     prose-strong:text-white prose-strong:font-semibold
                     prose-code:text-[#15FBFF] prose-code:bg-white/5 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                     prose-pre:bg-[#0a0a0a] prose-pre:border prose-pre:border-white/10
                     prose-a:text-[#5260FF] prose-a:underline hover:prose-a:text-[#7486FF]
                     prose-ul:list-disc prose-ol:list-decimal
                     prose-li:text-gray-300 prose-li:marker:text-gray-500
                     prose-blockquote:border-l-[#5260FF] prose-blockquote:text-gray-400
                     prose-table:border-collapse prose-table:border prose-table:border-white/10
                     prose-th:bg-white/5 prose-th:border prose-th:border-white/10 prose-th:p-2
                     prose-td:border prose-td:border-white/10 prose-td:p-2"
        >
          {content}
        </Streamdown>
      </StreamdownBoundary>
    </div>
  );
};

/**
 * StreamdownRendererSimple - For simple Markdown without advanced features
 * Use this for short summaries, descriptions, etc.
 */
export const StreamdownRendererSimple: React.FC<Omit<StreamdownRendererProps, 'mode'>> = ({
  content,
  className = ''
}) => {
  return (
    <StreamdownRenderer
      content={content}
      className={className}
      mode="static"
    />
  );
};
